import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { Button } from './ui/button';

export function Loading({ label }: { label?: string }) {
  const t = useT();
  return (
    <div role="status" className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label ?? t.common.loading}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const t = useT();
  const api = error instanceof ApiError ? error : null;
  if (api?.status === 403) return <Forbidden />;
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive-soft p-4 text-sm"
    >
      <p className="flex items-center gap-2 font-medium text-destructive">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        {api ? api.title : t.common.errorTitle}
      </p>
      {api?.detail ? <p>{api.detail}</p> : null}
      {api?.requestId ? (
        <p className="text-xs text-muted-foreground">
          {t.common.requestId} : <code>{api.requestId}</code>
        </p>
      ) : null}
      {onRetry ? (
        <div>
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t.common.retry}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Message d'erreur d'une action (formulaire, bouton) : titre et détail de l'API. */
export function ActionError({ error }: { error: Error | null }) {
  if (!error) return null;
  const message =
    error instanceof ApiError
      ? error.detail
        ? `${error.title} : ${error.detail}`
        : error.title
      : error.message;
  return (
    <p role="alert" className="text-sm text-destructive">
      {message}
    </p>
  );
}

export function Empty({ children }: { children?: ReactNode }) {
  const t = useT();
  return (
    <p className="p-6 text-center text-sm text-muted-foreground">{children ?? t.common.empty}</p>
  );
}

export function Forbidden() {
  const t = useT();
  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-xl border border-border bg-card p-6 text-sm"
    >
      <Lock className="h-4 w-4" aria-hidden />
      {t.common.forbidden}
    </div>
  );
}

/** Rendu d'une requête : chargement, erreur avec relance, puis contenu. */
export function QueryView<T>({
  query,
  children,
}: {
  query: { data: T | undefined; error: Error | null; isPending: boolean; refetch: () => unknown };
  children: (data: T) => ReactNode;
}) {
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.isPending || query.data === undefined) return <Loading />;
  return <>{children(query.data)}</>;
}
